import { useState } from "react";
import { useTranslation } from "react-i18next";
import ErrorNotice from "../../errors/ErrorNotice";
import { useSubmission } from "../../shared/state/useSubmission";
import AppButton from "../../ui/buttons/AppButton";
import { FORM_CARD_TIGHT_CLASS, FORM_CONTROL_CLASS, FORM_HELP_TEXT_CLASS, FORM_SELECT_CLASS } from "../../ui/styles/formStyles";
import BudgetFields from "./BudgetFields";
import { API_KINDS, addServerKey, budgetOf, type ApiKind } from "./serverKeysApi";
import Field from "../../ui/forms/Field";
import { deriveServerRouteId } from "./routeName";

/**
 * Add a server key with its monthly budget. The key field is write-only: the form clears it
 * once sent, and the server stores it only if the provider lists models with it.
 */
export default function KeyForm({ onAdded }: { onAdded: () => void }) {
  const { t } = useTranslation();
  const { state, submit } = useSubmission();
  const [routeName, setRouteName] = useState("");
  const [nameRequired, setNameRequired] = useState(false);
  const [api, setApi] = useState<ApiKind>("openai");
  const [baseUrl, setBaseUrl] = useState("");
  const [budget, setBudget] = useState({ calls: "", tokens: "" });
  const [apiKey, setApiKey] = useState("");
  const compatible = api === "openai_compatible";
  const routeId = deriveServerRouteId(routeName);
  const invalidName = routeId === null && (routeName !== "" || nameRequired);

  return (
    <form
      className={FORM_CARD_TIGHT_CLASS}
      autoComplete="off"
      onSubmit={(event) => {
        event.preventDefault();
        if (routeId === null) {
          setNameRequired(true);
          return;
        }
        const sent = apiKey;
        setApiKey("");
        submit(async () => {
          await addServerKey({ routeId, api, baseUrl: compatible ? baseUrl : null, ...budgetOf(budget), apiKey: sent });
          setRouteName("");
          setNameRequired(false);
          setBudget({ calls: "", tokens: "" });
          onAdded();
        });
      }}
    >
      <p className="text-sm font-semibold">{t("serverKeys.addKey")}</p>
      <Field
        label={t("serverKeys.routeName")}
        hint={<span aria-live="polite">{invalidName ? t("serverKeys.routeNameInvalid") : routeId === null ? t("serverKeys.routeNameHelp") : t("serverKeys.routeNameSavedAs", { routeId })}</span>}
      >
        {(control) => (
          <input {...control} className={FORM_CONTROL_CLASS} aria-required="true" aria-invalid={invalidName} placeholder="Anthropic Server" value={routeName} onChange={(event) => { setRouteName(event.target.value); }} />
        )}
      </Field>
      <Field label={t("serverKeys.provider")}>
        {(control) => (
          <select {...control} className={FORM_SELECT_CLASS} value={api} onChange={(event) => { setApi(event.target.value as ApiKind); }}>
            {API_KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {t(`serverKeys.api.${kind}`)}
              </option>
            ))}
          </select>
        )}
      </Field>
      {api === "gemini" ? <p className={FORM_HELP_TEXT_CLASS}>{t("serverKeys.geminiBilledOnly")}</p> : null}
      {compatible ? (
        <Field label={t("serverKeys.baseUrl")}>
          {(control) => (
            <input {...control} className={FORM_CONTROL_CLASS} type="url" required value={baseUrl} onChange={(event) => { setBaseUrl(event.target.value); }} />
          )}
        </Field>
      ) : null}
      <Field label={t("serverKeys.apiKey")}>
        {(control) => (
          <input {...control} className={FORM_CONTROL_CLASS} type="password" required minLength={8} autoComplete="new-password" value={apiKey} onChange={(event) => { setApiKey(event.target.value); }} />
        )}
      </Field>
      <BudgetFields value={budget} onChange={setBudget} />
      <p className={FORM_HELP_TEXT_CLASS}>{t("serverKeys.keyHelp")}</p>
      {state.status === "failed" ? <ErrorNotice error={state.error} /> : null}
      <AppButton type="submit" size="sm" variant="primary" disabled={state.status === "submitting"}>
        {t("serverKeys.addKey")}
      </AppButton>
    </form>
  );
}
